import React, { useEffect, Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Challenge } from '@/types';
import { useUserStore } from '@/stores/userStore';
import { useAppStore } from '@/stores/appStore';
import { HomeView } from '@/components/views/HomeView';
import { LoadingScreen } from '@/components/ui/LoadingSpinner';
import { ToastContainer, useToast } from '@/components/ui/Toast';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useChallenges, useCreateChallenge, useParticipants } from '@/hooks/useChallenges';
import { apiClient } from '@/lib/api';
import { PWAInstallPrompt } from '@/components/features/PWAInstallPrompt';

// Lazy load heavy components to reduce initial bundle size
const CreateChallengeView = lazy(() => import('@/components/views/CreateChallengeView'));
const JoinChallengeView = lazy(() => import('@/components/views/JoinChallengeView'));
const ShareView = lazy(() => import('@/components/views/ShareView'));
const ChallengeView = lazy(() => import('@/components/views/ChallengeView'));
const GymPassView = lazy(() => import('@/components/views/GymPassView'));
const PassLoginView = lazy(() => import('@/components/views/PassLoginView'));
const StatsView = lazy(() => import('@/components/views/StatsView'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 404s or authentication errors
        if (error instanceof Error && error.message.includes('404')) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 2 * 60 * 1000, // 2 minutes - reduced from 5 minutes for fresher data
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false, // Disabled to reduce unnecessary requests
      refetchOnReconnect: true, // Keep this for offline-online transitions
      refetchOnMount: false, // Don't always refetch on component mount
    },
    mutations: {
      retry: 1,
    },
  },
});

const AppContent: React.FC = () => {
  const { user, isAuthenticated, isLoading, initializeUser, loginWithUserId } = useUserStore();
  const { currentView, setCurrentView, setCurrentChallenge } = useAppStore();
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  // Monitor online/offline status
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  const { toasts, addToast, removeToast } = useToast();

  const handlePassLogin = async (userId: string) => {
    try {
      await loginWithUserId(userId);
      setCurrentView('home');
    } catch (error) {
      console.error('Failed to login with gym pass:', error);
      // Error handling is done in the userStore
    }
  };

  // Initialize user on app start
  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  // Start background sync when online
  useEffect(() => {
    if (isOnline) {
    }
  }, [isOnline]);

  // Show loading screen while initializing
  if (isLoading) {
    return <LoadingScreen message="Loading your fitness journey..." />;
  }

  // Show login if not authenticated and not on passlogin view
  if ((!isAuthenticated || !user) && currentView !== 'passlogin') {
    return <LoginView />;
  }

  // Special handling for passlogin view when not authenticated
  if (currentView === 'passlogin' && !isAuthenticated) {
    return (
      <>
        <Suspense fallback={<LoadingScreen message="Loading login..." />}>
          <PassLoginView onBack={() => setCurrentView('home')} onLoginSuccess={handlePassLogin} />
        </Suspense>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 min-h-screen">
      {/* Connection Status */}
      {!isOnline && (
        <div
          className="bg-yellow-500 text-white text-center py-2 text-sm font-medium"
          role="status"
          aria-live="polite"
          aria-label="Connection status"
        >
          You're offline. Changes will sync when connection is restored.
        </div>
      )}

      {/* Main Content */}
      <main role="main">
        <MainContent />
      </main>

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

const MainContent: React.FC = () => {
  const { currentView, currentChallenge, setCurrentView, setCurrentChallenge } = useAppStore();
  const {
    data: challenges = [],
    isLoading: challengesLoading,
    isFetching: challengesFetching,
  } = useChallenges();
  const { user, loginWithUserId } = useUserStore();
  const createChallengeMutation = useCreateChallenge();

  const handleCreateChallenge = async (
    challengeData: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const newChallenge = await createChallengeMutation.mutateAsync(challengeData);
      setCurrentChallenge(newChallenge);
      setCurrentView('share');
    } catch (error) {
      // Error handling is done in the mutation
    }
  };

  const handleJoinSuccess = (challengeId: string) => {
    console.log('🎉 Join success for challenge:', challengeId);
    console.log(
      '📋 Available challenges:',
      challenges.map(c => c.id)
    );

    const challenge = challenges.find(c => c.id === challengeId);
    if (challenge) {
      console.log('✅ Challenge found, navigating to challenge view');
      setCurrentChallenge(challenge);
      setCurrentView('challenge');
    } else {
      console.log('❌ Challenge not found in local challenges, going to home');
      // Try to refetch challenges and then navigate
      setTimeout(() => {
        const updatedChallenge = challenges.find(c => c.id === challengeId);
        if (updatedChallenge) {
          console.log('✅ Challenge found after delay, navigating to challenge view');
          setCurrentChallenge(updatedChallenge);
          setCurrentView('challenge');
        } else {
          console.log('❌ Challenge still not found, staying on home');
          setCurrentView('home');
        }
      }, 1000); // Give time for cache to update
    }
  };

  const handlePassLogin = async (userId: string) => {
    try {
      await loginWithUserId(userId);
      setCurrentView('home');
    } catch (error) {
      console.error('Failed to login with gym pass:', error);
      // Error handling is done in the userStore
    }
  };

  // For home view, we'll fetch participants individually per challenge
  // This is a simplified approach - could be optimized with batch requests
  const participantsMap = React.useMemo(() => {
    const map: Record<string, any> = {};
    // Initialize empty arrays for each challenge
    // Individual ChallengeCard components will fetch their own participant data
    challenges.forEach(challenge => {
      map[challenge.id] = [];
    });
    return map;
  }, [challenges]);

  switch (currentView) {
    case 'home':
      return (
        <HomeView
          challenges={challenges}
          isLoading={challengesLoading}
          onCreateChallenge={() => setCurrentView('create')}
          onJoinChallenge={() => setCurrentView('join')}
          onSelectChallenge={challenge => {
            setCurrentChallenge(challenge);
            setCurrentView('challenge');
          }}
          onShowGymPass={() => setCurrentView('gympass')}
          onShowStats={() => setCurrentView('stats')}
        />
      );

    case 'create':
      return (
        <Suspense fallback={<LoadingScreen message="Loading challenge creator..." />}>
          <CreateChallengeView
            onBack={() => setCurrentView('home')}
            onCreateChallenge={handleCreateChallenge}
          />
        </Suspense>
      );

    case 'join':
      return (
        <Suspense fallback={<LoadingScreen message="Loading challenge finder..." />}>
          <JoinChallengeView
            onBack={() => setCurrentView('home')}
            onJoinSuccess={handleJoinSuccess}
          />
        </Suspense>
      );

    case 'share':
      if (!currentChallenge) {
        setCurrentView('home');
        return null;
      }
      return (
        <Suspense fallback={<LoadingScreen message="Loading challenge sharing..." />}>
          <ShareView
            challenge={currentChallenge}
            onBack={() => setCurrentView('home')}
            onContinue={() => setCurrentView('challenge')}
          />
        </Suspense>
      );

    case 'challenge':
      if (!currentChallenge) {
        setCurrentView('home');
        return null;
      }
      return (
        <Suspense fallback={<LoadingScreen message="Loading challenge..." />}>
          <ChallengeView challenge={currentChallenge} onBack={() => setCurrentView('home')} />
        </Suspense>
      );

    case 'gympass':
      if (!user) {
        setCurrentView('home');
        return null;
      }
      return (
        <Suspense fallback={<LoadingScreen message="Loading gym pass..." />}>
          <GymPassView
            user={user}
            onBack={() => setCurrentView('home')}
            onContinue={() => setCurrentView('home')}
          />
        </Suspense>
      );

    case 'passlogin':
      return (
        <Suspense fallback={<LoadingScreen message="Loading login..." />}>
          <PassLoginView onBack={() => setCurrentView('home')} onLoginSuccess={handlePassLogin} />
        </Suspense>
      );

    case 'stats':
      return (
        <Suspense fallback={<LoadingScreen message="Loading your stats..." />}>
          <StatsView onBack={() => setCurrentView('home')} />
        </Suspense>
      );

    default:
      return (
        <HomeView
          challenges={challenges}
          isLoading={challengesLoading}
          onCreateChallenge={() => setCurrentView('create')}
          onJoinChallenge={() => setCurrentView('join')}
          onSelectChallenge={challenge => {
            setCurrentChallenge(challenge);
            setCurrentView('challenge');
          }}
          onShowGymPass={() => setCurrentView('gympass')}
          onShowStats={() => setCurrentView('stats')}
        />
      );
  }
};

const LoginView: React.FC = () => {
  const [username, setUsername] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { login } = useUserStore();
  const { setCurrentView } = useAppStore();
  const { addToast } = useToast();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      addToast({
        type: 'error',
        message: 'Please enter a username',
      });
      return;
    }

    setIsLoading(true);
    try {
      const user = await login(username.trim());
      // Show gym pass after successful registration
      setCurrentView('gympass');
    } catch (error) {
      addToast({
        type: 'error',
        message: 'Failed to login. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <p className="text-slate-800 text-lg font-semibold mb-4">Welcome to</p>
          <div className="pushing-pencils-title mb-4 mx-auto w-fit">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl overflow-hidden">
              <img
                src="/logo.png"
                alt="Pushing Pencils Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="pushing-pencils-text" style={{ fontSize: '2.5rem' }}>
              Pushing Pencils
            </div>
          </div>
          <p className="text-slate-600 font-heading text-xl">
            "COME WITH ME IF YOU WANT TO LIFT!" 💪
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Choose a username
            </label>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white/80"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-6 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Getting Started...' : 'Start Your Journey'}
          </button>
        </form>

        {/* Divider */}
        <div className="my-6 flex items-center">
          <div className="flex-1 border-t border-slate-300"></div>
          <span className="px-4 text-sm text-slate-500">or</span>
          <div className="flex-1 border-t border-slate-300"></div>
        </div>

        {/* Login with Gym Pass */}
        <button
          onClick={() => setCurrentView('passlogin')}
          className="w-full bg-white border-2 border-slate-300 text-slate-700 py-3 px-6 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-300 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
        >
          <span className="text-xl">🎫</span>
          Login with Gym Pass
        </button>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
